import TimePanelHeaderButtonWell from './TimePanelHeaderButtonWell';
import { updateCurrentView } from '../actions/timePanelStoreActions';
import {
    getCurrentPanelViewType,
    getStackLength,
    getCurrentPanelView,
} from 'owa-time-panel-view-stack';
import { observer } from 'mobx-react-lite';
import { ActionButton } from 'office-ui-fabric-react/lib/Button';
import { Pivot, PivotItem, PivotLinkSize, IPivotItemProps } from 'office-ui-fabric-react/lib/Pivot';
import { ControlIcons } from 'owa-control-icons';
import loc from 'owa-localize';
import * as React from 'react';
import {
    backButtonText,
    backButtonTitle,
    calendarPivotText,
    tasksPivotText,
} from './TimePanelHeaderBar.locstring.json';
import { Droppable, DragData } from 'owa-dnd';
import {
    getCalendarTabDropState,
    shouldShowCalendarTabHoverHint,
    setLastEnterCalendarTab,
    getLastDragOverCalendarTab,
    resetHeaderTabDropViewState,
    getTasksTabDropState,
    shouldShowTasksTabHoverHint,
    setLastEnterTasksTab,
    getLastDragOverTasksTab,
    TimePanelDropListener,
    dropMailItem,
} from 'owa-time-panel-dnd';
import { isFeatureEnabled } from 'owa-feature-flags';
import { popView } from 'owa-time-panel-bootstrap';
import { closeComposeFormInTimePanel } from 'owa-time-panel-compose';
import { now, addMilliseconds, compare } from 'owa-datetime';
import { MailListRowDragData } from 'owa-mail-types/lib/types/MailListRowDragData';

const styles = require('./TimePanelHeaderBar.scss');
const classNames = require('classnames/bind').bind(styles);
const HOVER_WAIT_TIME = 500;

export default observer(function TimePanelHeaderBar() {
    return (
        <TimePanelDropListener>
            <div className={styles.container}>
                <div className={styles.headerControl}>
                    {getStackLength() > 1 ? (
                        <ActionButton
                            className={styles.backButton}
                            styles={{
                                icon: styles.backButtonIcon,
                            }}
                            text={loc(backButtonText)}
                            title={loc(backButtonTitle)}
                            onClick={backButtonClicked}
                            iconProps={{ iconName: ControlIcons.Back }}
                        />
                    ) : (
                        <Pivot
                            headersOnly={true}
                            onLinkClick={selectPivot}
                            linkSize={PivotLinkSize.normal}
                            selectedKey={getCurrentPanelViewType()}
                            styles={{ linkIsSelected: styles.pivotLinkSelected }}>
                            {!isFeatureEnabled('cal-time-panel-dragEscalateMeeting') ? (
                                <PivotItem
                                    itemKey={'Calendar'}
                                    headerText={loc(calendarPivotText)}
                                />
                            ) : (
                                <PivotItem
                                    itemKey={'Calendar'}
                                    headerText={loc(calendarPivotText)}
                                    onRenderItemLink={renderDroppableCalendarPivot}
                                    className={calendarPivotStyles()}
                                />
                            )}
                            <PivotItem
                                itemKey={'Tasks'}
                                headerText={loc(tasksPivotText)}
                                onRenderItemLink={renderDroppableTasksPivot}
                                className={tasksPivotStyles()}
                            />
                        </Pivot>
                    )}
                </div>
                <TimePanelHeaderButtonWell />
            </div>
        </TimePanelDropListener>
    );
});

function backButtonClicked(): void {
    if (getCurrentPanelView() === 'ComposeForm') {
        // If we have compose form opened, then we call closeComposeFormInTimePanel so that we show confirmation dialog if required.
        // closeComposeFormInTimePanel will take care of calling popView when it's done
        closeComposeFormInTimePanel();
    } else {
        popView();
    }
}

function selectPivot(item: PivotItem): void {
    switch (item.props.itemKey) {
        case 'Calendar':
            updateCurrentView('Calendar');
            break;
        case 'Tasks':
            updateCurrentView('Tasks');
            break;
        default:
            throw new Error('Not a valid ItemKey: ' + item.props.itemKey);
    }
}

/**
 * We don't allow dropping onto the tab, only hovering to open Tasks
 */
function doNothing(dragInfo: DragData, pageX: number, pageY: number, target?: HTMLElement) {
    return;
}

function dropOnCalendarTab(dragInfo: DragData, pageX: number, pageY: number, target?: HTMLElement) {
    const mailListDragData = dragInfo as MailListRowDragData;
    dropMailItem(
        mailListDragData,
        'TimePanel_DragToCalendarTabReplyWithMeeting',
        'CalendarTabDropFallback'
    );
}

function startHoverCalendarTab(dragInfo: DragData): void {
    setLastEnterCalendarTab(now());
}

function openCalendarAfterHover(): void {
    if (compare(addMilliseconds(getLastDragOverCalendarTab(), HOVER_WAIT_TIME), now()) <= 0) {
        resetHeaderTabDropViewState(getCurrentPanelView(), 'Calendar');
        updateCurrentView('Calendar');
    }
}

function startHoverTasksTab(dragInfo: DragData): void {
    setLastEnterTasksTab(now());
}

function openTasksAfterHover(): void {
    if (compare(addMilliseconds(getLastDragOverTasksTab(), HOVER_WAIT_TIME), now()) <= 0) {
        resetHeaderTabDropViewState(getCurrentPanelView(), 'Tasks');
        updateCurrentView('Tasks');
    }
}

function calendarPivotStyles(): string {
    return classNames(
        { dropContainer: shouldShowCalendarTabHoverHint() },
        { dropHoverTreatment: getCalendarTabDropState().isDragOver }
    );
}

function tasksPivotStyles(): string {
    return classNames(
        { dropContainer: shouldShowTasksTabHoverHint() },
        { dropHoverTreatment: getTasksTabDropState().isDragOver }
    );
}

function renderDroppableCalendarPivot(
    link: IPivotItemProps,
    defaultRenderer: (link: IPivotItemProps) => JSX.Element
): JSX.Element {
    return getCurrentPanelViewType() !== 'Calendar' ? (
        <Droppable
            dropViewState={getCalendarTabDropState()}
            onDrop={dropOnCalendarTab}
            onDragEnter={startHoverCalendarTab}
            onDragOver={openCalendarAfterHover}>
            <div className={calendarPivotStyles()}>{defaultRenderer(link)}</div>
        </Droppable>
    ) : (
        defaultRenderer(link)
    );
}

function renderDroppableTasksPivot(
    link: IPivotItemProps,
    defaultRenderer: (link: IPivotItemProps) => JSX.Element
): JSX.Element {
    return getCurrentPanelViewType() !== 'Tasks' ? (
        <Droppable
            dropViewState={getTasksTabDropState()}
            onDrop={doNothing}
            onDragEnter={startHoverTasksTab}
            onDragOver={openTasksAfterHover}>
            <div className={tasksPivotStyles()}>{defaultRenderer(link)}</div>
        </Droppable>
    ) : (
        defaultRenderer(link)
    );
}
